/**
 * Build a Google Maps multi-stop directions URL.
 *
 * @param {Array<{latitude:number, longitude:number}>} stops
 * @param {{depot?: {latitude:number, longitude:number} | null}} options
 *        When `depot` is provided, the route is prepended AND appended with
 *        that location (start & end at the bus depot).
 * @returns {string|null} URL string, or null if fewer than 2 valid points.
 */
export function buildGoogleMapsRouteUrl(stops, options = {}) {
  const valid = (stops || []).filter(
    (s) => typeof s?.latitude === "number" && typeof s?.longitude === "number"
  );
  const depot = options?.depot;
  const depotValid =
    depot &&
    typeof depot.latitude === "number" &&
    typeof depot.longitude === "number";
  const ordered = depotValid
    ? [depot, ...valid.slice(0, 9), depot]
    : valid.slice(0, 9);
  if (ordered.length < 2) return null;
  const path = ordered
    .map((s) => `${s.latitude.toFixed(6)},${s.longitude.toFixed(6)}`)
    .join("/");
  return `https://www.google.com/maps/dir/${path}`;
}

/**
 * Plain-text dump of stops for pasting into a trucking app (Trucker Path,
 * HERE WeGo, etc). One stop per line: "1. Name — Address, City, CA".
 *
 * @param {Array<{name:string, address?:string, city?:string}>} stops
 * @returns {string}
 */
export function stopsAsText(stops) {
  return (stops || [])
    .map((s, i) => {
      const parts = [s.address, s.city, "CA"].filter(Boolean).join(", ");
      return `${i + 1}. ${s.name || "Stop"}${parts ? " — " + parts : ""}`;
    })
    .join("\n");
}
